import s from './Personalization.module.css';
import { Modal } from 'components/common/Modal/Modal';
import { ModalNavigationContext } from 'components/pages/ConsultantPage/ConsultantPage';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InputField } from 'components/common/InputField';
import { Button } from 'components/common/Button/Button';
import { ReactComponent as Stars } from 'images/stars.svg';
import { useAppSelector } from 'store/hooks';
import {
  selectAddressTypes,
  selectUserAddressName,
  selectUserAddressType,
  selectUserInstruction,
} from 'store/features/user/selectors';
import { ChangeEventHandler, useMemo, useState } from 'react';
import { useUpdateUserPersonalizationMutation } from 'store/features/user/user-api';
import { ErrorModal } from 'components/common/ErrorModal/ErrorModal';
import { useQueryError } from 'lib/hooks/useQueryError';
import { Loader } from 'components/common/Loader/Loader';
import { ScrollableContainer } from 'components/common/ScrollableContainer';
import { OptionListModal } from 'components/common/OptionListModal/OptionListModal';
import { OptionRow } from 'components/common/OptionRow/OptionRow';
import { AddressType } from 'types';

const icons = {
  stars: <Stars />,
};

export const Personalization = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { closeRouteModal, goBack } =
    useOutletContext<ModalNavigationContext>();

  const userAddressName = useAppSelector(selectUserAddressName);
  const userAddressType = useAppSelector(selectUserAddressType);
  const userInstruction = useAppSelector(selectUserInstruction);
  const addressTypes = useAppSelector(selectAddressTypes);

  const [
    updateUserPersonalization,
    {
      isLoading: isUpdateUserPersonalizationLoading,
      isError: isUpdateUserPersonalizationError,
      error: updateUserPersonalizationError,
    },
  ] = useUpdateUserPersonalizationMutation();

  const { showError, errorMessage, closeError } = useQueryError(
    isUpdateUserPersonalizationError,
    updateUserPersonalizationError,
  );

  const [addressName, setAddressName] = useState<string>(userAddressName);
  const [addressTypeId, setAddressTypeId] = useState<string>(userAddressType);
  const [isStyleOpen, setIsStyleOpen] = useState(false);

  const selectedAddressType = useMemo(
    () => addressTypes.find((option) => option.id === addressTypeId) ?? null,
    [addressTypes, addressTypeId],
  );

  const isDisabled =
    userAddressName === addressName.trim() &&
    userAddressType === selectedAddressType?.id;

  const handleAddressNameChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setAddressName(e.target.value);
  };

  const handleOpenInstruction = () => {
    navigate('instruction');
  };

  const handleOpenCommunicationStyle = () => {
    setIsStyleOpen(true);
  };

  const handleCloseCommunicationStyle = () => {
    setIsStyleOpen(false);
  };

  const handleAddressTypeSelect = (id: string) => {
    setAddressTypeId(id as AddressType);
    setIsStyleOpen(false);
  };

  const handleApply = async () => {
    try {
      await updateUserPersonalization({
        addressName: addressName.trim(),
        addressType: selectedAddressType?.id ?? 'standard',
      }).unwrap();
    } catch {
      // console.error(error);

      setAddressName(userAddressName);
      setAddressTypeId(userAddressType);
    }
  };

  if (isStyleOpen) {
    return (
      <OptionListModal
        title={t('profile.personalization.communicationStyle')}
        options={addressTypes}
        selectedId={addressTypeId}
        onSelect={handleAddressTypeSelect}
        onBack={handleCloseCommunicationStyle}
        onClose={closeRouteModal}
      />
    );
  }

  return (
    <Modal
      title={t('profile.personalization.title')}
      onClose={closeRouteModal}
      onBack={goBack}
    >
      <div className={s['container']}>
        <ScrollableContainer
          className={s['scrollable-container']}
          scrollHeight={'45rem'}
        >
          <InputField
            title={t('profile.personalization.callName')}
            value={addressName}
            onChange={handleAddressNameChange}
            placeholder={t('common.notSet')}
          />

          <OptionRow
            title={t('profile.personalization.communicationStyle')}
            value={selectedAddressType?.name ?? t('common.notSet')}
            onClick={handleOpenCommunicationStyle}
          />

          <Button
            className={s['add-instruction-btn']}
            onClick={handleOpenInstruction}
          >
            {icons.stars}
            <span>
              {userInstruction
                ? t('profile.personalization.editInstruction')
                : t('profile.personalization.addInstruction')}
            </span>
          </Button>

          {userInstruction && (
            <div className={s['instruction']}>{userInstruction}</div>
          )}
        </ScrollableContainer>

        <Button
          className={s['apply-btn']}
          onClick={handleApply}
          isAccent={true}
          isRounded={true}
          disabled={isDisabled}
        >
          {t('common.apply')}
        </Button>
      </div>

      {isUpdateUserPersonalizationLoading && <Loader />}

      {showError && <ErrorModal message={errorMessage} onClose={closeError} />}
    </Modal>
  );
};
